import { Attributes, SpanStatusCode, trace } from '@opentelemetry/api'

import { APP_NAME } from '../../config/costants'
import { EventsAPI } from './types'

/**
 * The `initializeEventsAPI` function initializes an EventsAPI object with a `pushEvent` method that
 * creates a span and adds an event to it.
 * @returns The function `initializeEventsAPI` returns an object with a single property `pushEvent`.
 */
export function initializeEventsAPI(): EventsAPI {
  /**
   * The function `pushEvent` creates a span and adds an event with metadata to it, then ends the span.
   * @param evt - The `evt` parameter is the name of the event that is being pushed. It is a string
   * value.
   * @param metas - The `metas` parameter is an optional object that represents additional metadata
   * associated with the event. It can contain any key-value pairs that provide more context or
   * information about the event.
   */
  const pushEvent: EventsAPI['pushEvent'] = (evt, metas = {}) => {
    const tracer = trace.getTracer(APP_NAME)
    // Create a span. A span must be closed.
    tracer.startActiveSpan(evt ?? 'CUSTOM_EVENT', (span) => {
      span.addEvent(evt, metas as Attributes, performance.now())
      span.setStatus({ code: SpanStatusCode.UNSET })
      span.end()
    })
  }

  const runInEventSpan: EventsAPI['runInEventSpan'] = (evt, fn, attributes = {}) => {
    const tracer = trace.getTracer(APP_NAME)
    return tracer.startActiveSpan(evt, { attributes }, async (span) => {
      try {
        return await fn()
      } catch (error) {
        span.recordException(error as Error)
        span.setStatus({ code: SpanStatusCode.ERROR })
        throw error
      } finally {
        span.end()
      }
    })
  }

  return {
    pushEvent,
    runInEventSpan,
  }
}
